import { parseDurationToMinutes } from './appointmentBookingUtils';

export const ServiceValueMode = {
  Fixed: 'Fixed',
  Range: 'Range',
  OnRequest: 'OnRequest',
};

export const DURATION_MIN_MINUTES = 5;
export const DURATION_MAX_MINUTES = 480;
export const DURATION_STEP_MINUTES = 5;

export const normalizeServiceValueMode = (mode) => {
  if (mode === ServiceValueMode.Range || mode === 'Range') return ServiceValueMode.Range;
  if (mode === ServiceValueMode.OnRequest || mode === 'OnRequest') return ServiceValueMode.OnRequest;
  return ServiceValueMode.Fixed;
};

export const serviceAllowsPlanOrPackage = (service) => (
  normalizeServiceValueMode(service?.priceMode ?? service?.PriceMode) === ServiceValueMode.Fixed
  && normalizeServiceValueMode(service?.durationMode ?? service?.DurationMode) === ServiceValueMode.Fixed
);

export const usesCatalogPrice = (service) => (
  Boolean(service?.isCombo)
  || normalizeServiceValueMode(service?.priceMode ?? service?.PriceMode) === ServiceValueMode.Fixed
);

const readDurationMax = (service) => {
  const max = service?.durationMax ?? service?.DurationMax;
  return max == null || max === '' ? null : max;
};

export const hasCustomerBookingDuration = (service) => {
  if (service?.isCombo) return true;
  const mode = normalizeServiceValueMode(service?.durationMode ?? service?.DurationMode);
  return mode === ServiceValueMode.Fixed
    || (mode === ServiceValueMode.Range && readDurationMax(service) != null);
};

// A range-duration service is booked by the customer for its maximum; the store can shorten it later.
export const withCustomerBookingDuration = (service) => {
  const mode = normalizeServiceValueMode(service?.durationMode ?? service?.DurationMode);
  const max = readDurationMax(service);
  if (service?.isCombo || mode !== ServiceValueMode.Range || max == null) return service;
  return { ...service, duration: parseDurationToMinutes(max) };
};

export const selectCustomerBookableServices = (services, serviceIds) => (
  (services || []).filter((service) => serviceIds?.includes(service.id) && hasCustomerBookingDuration(service))
);

export const assignedServicePriceHint = (service, teamMember) => {
  if (!service || normalizeServiceValueMode(service.priceMode ?? service.PriceMode) !== ServiceValueMode.Fixed) {
    return undefined;
  }
  const assigned = (teamMember?.services || []).find((item) => (
    item.id === service.id || item.serviceId === service.id
  ));
  if (assigned?.customPrice != null && assigned.customPrice !== '') return assigned.customPrice;
  return service.price;
};

export const formatClockMinutes = (minutes) => {
  const total = Math.max(0, Number(minutes) || 0);
  const hours = Math.floor(total / 60);
  const remaining = total % 60;
  return `${String(hours).padStart(2, '0')}:${String(remaining).padStart(2, '0')}`;
};

export const minutesToApiDuration = (minutes) => formatClockMinutes(minutes);

export const buildDurationMinuteOptions = () => {
  const options = [];
  for (let minutes = DURATION_MIN_MINUTES; minutes <= DURATION_MAX_MINUTES; minutes += DURATION_STEP_MINUTES) {
    options.push({ value: minutes, label: formatClockMinutes(minutes) });
  }
  return options;
};

export const isSchedulableDuration = (minutes) => {
  const value = Number(minutes);
  return Number.isInteger(value)
    && value >= DURATION_MIN_MINUTES
    && value <= DURATION_MAX_MINUTES
    && value % DURATION_STEP_MINUTES === 0;
};

export const isLineQuoteDurationInvalid = (service, quote) => {
  if (!service || service.isCombo) return false;
  const chosen = quote?.durationMinutes;
  const hasChoice = chosen !== '' && chosen != null;
  if (hasChoice) return !isSchedulableDuration(Number(chosen));
  const mode = normalizeServiceValueMode(service.durationMode ?? service.DurationMode);
  const catalogMinutes = parseDurationToMinutes(service.duration ?? service.Duration);
  return !(mode === ServiceValueMode.Fixed && catalogMinutes === 0);
};

const readNumber = (service, ...keys) => {
  for (const key of keys) {
    if (service?.[key] != null && service[key] !== '') return service[key];
  }
  return 0;
};

const readOptionalNumber = (service, ...keys) => {
  for (const key of keys) {
    const value = service?.[key];
    if (value == null || value === '') continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

export const isPriceOutsideServiceRange = (service, price) => {
  if (normalizeServiceValueMode(service?.priceMode ?? service?.PriceMode) !== ServiceValueMode.Range) {
    return false;
  }
  const value = Number(price);
  if (!Number.isFinite(value)) return false;
  const min = readOptionalNumber(service, 'catalogPrice', 'price', 'Price');
  const max = readOptionalNumber(service, 'priceMax', 'PriceMax');
  if (min != null && value < min) return true;
  return max != null && value > max;
};

export const formatServicePriceLabel = (service, { formatMoney, onRequestLabel, freeLabel, fromLabel }) => {
  const mode = normalizeServiceValueMode(service?.priceMode ?? service?.PriceMode);
  if (mode === ServiceValueMode.OnRequest) return onRequestLabel;
  const price = readNumber(service, 'price', 'Price');
  if (mode === ServiceValueMode.Range) {
    const max = readOptionalNumber(service, 'priceMax', 'PriceMax');
    if (max == null) {
      const formatted = formatMoney(price);
      return fromLabel ? fromLabel(formatted) : formatted;
    }
    return `${formatMoney(price)} - ${formatMoney(max)}`;
  }
  if (Number(price) === 0 && freeLabel) return freeLabel;
  return formatMoney(price);
};

export const formatServiceDurationLabel = (service, { onRequestLabel }) => {
  const mode = normalizeServiceValueMode(service?.durationMode ?? service?.DurationMode);
  if (mode === ServiceValueMode.OnRequest) return onRequestLabel;
  const min = formatClockMinutes(parseDurationToMinutes(service?.duration ?? service?.Duration));
  if (mode === ServiceValueMode.Range) {
    const max = formatClockMinutes(parseDurationToMinutes(service?.durationMax ?? service?.DurationMax));
    return `${min} - ${max}`;
  }
  return min;
};

export const UNPRICED_CONFIRMED_COLORS = {
  background: '#ede9fe',
  border: '#7c3aed',
  text: '#5b21b6',
};

export const resolveAgendaStatusColors = (statusKey, hasUnpricedServices, fallback) => {
  if (statusKey === 'confirmed' && hasUnpricedServices) return UNPRICED_CONFIRMED_COLORS;
  return fallback;
};
